import { useRef, useEffect } from "react";
import { Panel } from "../../../components/panels/Panel";
import "../../../styles/dior/digital_twin.scss";
import {
  ALL_POOLS,
  getCartColor, getSchemeId, getSchemeIdFromTechnicalSchemeId,
  POOLS_LINE_1, POOLS_LINE_2
} from "../../../services/live-interface/dior/dior-interface-service";
import {
  checkProcessDivergences,
  getTheoreticalMatchFromCartHistory
} from "../../../services/live-interface/dior/dior-check-errors";
import { formatSecondsToMinutesSeconds } from "../../../helpers/date/date-helper";
import { ComputerDesktopIcon, PlusIcon } from "@heroicons/react/20/solid";

const STEP_MS = 40;
const AUTO_SCROLL_SPEED = 80;        // px/s
const AUTO_SCROLL_MARGIN = 80;       // px
const USER_SCROLL_PAUSE_MS = 5000;   // ms

const DiorMiniProgressBar = ({ value, maxValue, color }) => {
  const percent = Math.floor(value / maxValue * 100, 100);
  return (
    <div className="w-full flex flex-col items-center gap-1">
      <div className="w-full bg-white/50 h-2 rounded-full overflow-hidden">
        <div className="h-full rounded-md" style={{ width: percent + '%', background: color}}/>
      </div>
      <div className="text-xs" style={{ color: color }}>
        {/*{`${formatSecondsToMinutesSeconds(value)}/${formatSecondsToMinutesSeconds(maxValue)}`}*/}
        {`${formatSecondsToMinutesSeconds(value)}`}
      </div>
    </div>
  );
};

const DiorLine = ({ pools, cartsList, startPoolSchemeIndex=0, positionMode, registerPoolRef, onPoolClick=null, processes=[] }) => {
  const reverse = (positionMode === 'up' && startPoolSchemeIndex > 0) || (positionMode === 'bottom' && !(startPoolSchemeIndex > 0));
  return (
    <div className="bg-perception-black-500 flex flex-col h-[240px] gap-4 p-4 rounded-md">
      <div className={`flex-1 flex gap-1 flex-nowrap min-w-max h-full justify-start ${reverse ? 'flex-row-reverse' : ''}`}>
        {
          pools.map((pool, _poolSchemeIndex) => {
            if (pool?.duplicate_of !== undefined) return null;
            const poolSchemeIndex = getSchemeIdFromTechnicalSchemeId(_poolSchemeIndex + startPoolSchemeIndex);
            // if (pool?.skip === true) return null;

            const cartsInPool = cartsList.filter((cart) => {
              const lastStep = cart.history?.[cart.history.length - 1];
              if (!lastStep) return false;
              return getSchemeId(lastStep.pool_id) === poolSchemeIndex && lastStep.done === false;
            });

            const subPoolsNumber = pool?.sub_pools || 1;
            const hasSubPools = pool?.sub_pools > 0;
            const width = hasSubPools ? 'w-8' : 'w-16';

            const cartsData = Array.from({ length: cartsInPool.length }, () => ({ color: '#232222' }));

            // These variables are shared between all cartsInPool
            let isPoolError = false, isPoolMinDurationOver = false;

            for (let i=0; i<cartsInPool.length; i++) {
              const cartInPool = cartsInPool[i];
              const cartHistorySchemeId = cartInPool.history.map((step) => ({...step, pool_id: getSchemeId(step.pool_id)}));

              const lastStep = (() => {
                if (!cartInPool?.history || cartInPool.history.length === 0) return null;
                return cartInPool.history[cartInPool.history.length - 1];
              })();
              cartsData[i].lastStep = lastStep;

              let process = processes.find((process) => process.name === cartInPool.rack_type)?.pattern_data?.[0]?.json;
              if (!process) process = [];

              const { theoreticalStep } = getTheoreticalMatchFromCartHistory(cartHistorySchemeId, process);
              const theoreticalDuration = theoreticalStep?.duration_min ?? 0;
              cartsData[i].theoreticalDuration = theoreticalDuration;

              const cartValue = lastStep?.value ?? null;
              const minDurationOver = cartValue > theoreticalDuration;
              isPoolMinDurationOver = isPoolMinDurationOver || minDurationOver;

              const { steps_error } = checkProcessDivergences(process, cartHistorySchemeId);
              const isCurrentPoolError = steps_error.find((error) => error.details.pool_id === poolSchemeIndex) !== undefined;
              isPoolError = isPoolError || isCurrentPoolError;

              cartsData[i].color = isCurrentPoolError ? '#f9224b' : minDurationOver ? '#17E15B' : getCartColor();
            }

            const shouldBlink = isPoolError || isPoolMinDurationOver;
            const borderColor = isPoolError ? '#f9224b' : isPoolMinDurationOver ? '#17E15B' : '#232222';

            return (
              <div key={poolSchemeIndex}
                   ref={el => registerPoolRef && registerPoolRef(poolSchemeIndex, el)}
                   className={`group relative flex-shrink-0 h-full flex flex-col items-center border-2 gap-2 p-2 rounded-md ${shouldBlink ? 'blink-border' : ''}`}
                   style={{ '--blink-border-color': borderColor, borderColor: borderColor }}
                   onClick={() => onPoolClick?.(pool, poolSchemeIndex)}
              >
                {
                  pool.screen_position !== undefined &&
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-perception-blue p-2 rounded-md z-10"
                         style={{ marginLeft: reverse ? -pool.screen_position : pool.screen_position }}>
                      <ComputerDesktopIcon className="size-6 text-white"/>
                    </div>
                }
                {
                  onPoolClick &&
                    <div className="flex flex-col justify-center items-center text-center absolute inset-0 opacity-0 z-10 group-hover:opacity-100
                      transition-opacity backdrop-blur-sm bg-perception-blue/30 text-xs cursor-pointer">
                      <PlusIcon className="size-8"/>
                      {pool.name}
                    </div>
                }
                <div className="text-xs bg-perception-gray-700 text-white px-3 py-0.5 rounded-md">
                  {poolSchemeIndex}
                </div>
                <div className="text-xs">
                  {pool.name}
                </div>
                <div className="flex gap-1 h-full relative">
                  {
                    Array.from({ length: subPoolsNumber }).map((subPool, subPoolIndex) =>
                      <div key={subPoolIndex} className={`${width} flex-shrink-0 bg-perception-black-800 rounded-md`}/>
                    )
                  }
                  <div className="absolute top-0 left-0 w-full h-full flex flex-col gap-2 justify-center items-center">
                    {
                      cartsInPool.map((_, cartInPoolIndex) =>
                        <div key={cartInPoolIndex} className="w-3/4 flex justify-center text-white backdrop-blur-sm bg-white/10 rounded-md p-1">
                          <DiorMiniProgressBar value={cartsData[cartInPoolIndex].lastStep?.value}
                                               maxValue={cartsData[cartInPoolIndex].theoreticalDuration}
                                               color={cartsData[cartInPoolIndex].color}
                          />
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  );
};

export const DiorDigitalTwin = ({ cartsList, onPoolClick=null, processes=[], positionMode='up', hasDoubt=false }) => {
  // Refs for auto-scroll
  const scrollContainerRef = useRef(null);
  const poolRefs = useRef({});
  const directionRef = useRef(1);
  const userScrollingRef = useRef(false);
  const userScrollTimeoutRef = useRef(null);
  const scrollPositionRef = useRef(0);

  const registerPoolRef = (poolSchemeIndex, el) => {
    if (el) poolRefs.current[poolSchemeIndex] = el;
    else delete poolRefs.current[poolSchemeIndex];
  };

  const handleUserInteraction = () => {
    // L'utilisateur interagit → on met l'auto-scroll en pause
    userScrollingRef.current = true;

    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }

    userScrollTimeoutRef.current = setTimeout(() => {
      // Fin de la pause → on laisse l’animation reprendre naturellement
      userScrollingRef.current = false;
    }, USER_SCROLL_PAUSE_MS);
  };

  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 1. Trouver les pools actifs
    const activeIndicesSet = new Set();

    cartsList.forEach((cart) => {
      const lastStep = cart.history && cart.history[cart.history.length - 1];
      if (!lastStep || lastStep.done) return;
      const schemeIndex = getSchemeId(lastStep.pool_id);
      activeIndicesSet.add(schemeIndex);
    });

    const activeIndices = Array.from(activeIndicesSet);
    if (!activeIndices.length) return;

    const containerRect = container.getBoundingClientRect();

    // ✅ Si tous les pools non vides sont déjà visibles, on ne scroll pas
    const viewportWidth = container.clientWidth;
    const maxNativeScroll = Math.max(0, container.scrollWidth - viewportWidth);

    let minLeftVisibleCheck = Infinity;
    let maxRightVisibleCheck = -Infinity;

    activeIndices.forEach((index) => {
      const el = poolRefs.current[index];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const leftInContent = rect.left - containerRect.left + container.scrollLeft;
      const rightInContent = rect.right - containerRect.left + container.scrollLeft;
      if (leftInContent < minLeftVisibleCheck) minLeftVisibleCheck = leftInContent;
      if (rightInContent > maxRightVisibleCheck) maxRightVisibleCheck = rightInContent;
    });

    if (minLeftVisibleCheck !== Infinity && maxRightVisibleCheck !== -Infinity) {
      const fullyVisible =
        minLeftVisibleCheck >= container.scrollLeft &&
        maxRightVisibleCheck <= container.scrollLeft + viewportWidth;

      if (fullyVisible) {
        const clamped = Math.max(0, Math.min(container.scrollLeft, maxNativeScroll));
        container.scrollLeft = clamped;
        scrollPositionRef.current = clamped;
        return; // ⬅️ pas d'interval -> pas d'auto-scroll
      }
    }

    // CAS PARTICULIER : un seul pool actif -> on le centre et on ne scroll pas
    if (activeIndices.length === 1) {
      const poolIndex = activeIndices[0];
      const el = poolRefs.current[poolIndex];
      if (!el) return;

      const rect = el.getBoundingClientRect();

      // centre du pool dans le contenu scrollable
      const poolCenterInContent =
        (rect.left + rect.right) / 2 - containerRect.left + container.scrollLeft;

      let targetScrollLeft = poolCenterInContent - viewportWidth / 2;
      if (targetScrollLeft < 0) targetScrollLeft = 0;
      if (targetScrollLeft > maxNativeScroll) targetScrollLeft = maxNativeScroll;

      container.scrollLeft = targetScrollLeft;
      scrollPositionRef.current = targetScrollLeft;
      return; // ⬅️ pas d'interval -> pas d'auto-scroll
    }

    // 2. Calculer les bornes min/max en pixels (cas général : plusieurs pools actifs)
    let minLeft = Infinity;
    let maxRight = -Infinity;

    activeIndices.forEach((index) => {
      const el = poolRefs.current[index];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const leftInContent = rect.left - containerRect.left + container.scrollLeft;
      const rightInContent = rect.right - containerRect.left + container.scrollLeft;
      if (leftInContent < minLeft) minLeft = leftInContent;
      if (rightInContent > maxRight) maxRight = rightInContent;
    });

    if (minLeft === Infinity || maxRight === -Infinity) return;

    const leftBound = Math.max(0, minLeft - AUTO_SCROLL_MARGIN);
    const rightBound = Math.min(container.scrollWidth, maxRight + AUTO_SCROLL_MARGIN);

    const contentWidth = rightBound - leftBound;

    let minScroll;
    let maxScroll;

    if (contentWidth <= viewportWidth) {
      // La zone d'activité tient dans l'écran : on fait un ping-pong autour
      const extra = viewportWidth - contentWidth;
      const center = leftBound - extra / 2;
      minScroll = Math.max(0, center - AUTO_SCROLL_MARGIN);
      maxScroll = center + AUTO_SCROLL_MARGIN;
    } else {
      // Zone plus large que l'écran : ping-pong entre la gauche et la droite
      minScroll = leftBound;
      maxScroll = Math.max(leftBound, rightBound - viewportWidth);
    }

    // Clamp dans la plage réellement scrollable
    minScroll = Math.max(0, Math.min(minScroll, maxNativeScroll));
    maxScroll = Math.max(0, Math.min(maxScroll, maxNativeScroll));

    if (maxScroll <= minScroll) {
      // Rien à scroller
      return;
    }

    // 3. Position de départ dans la plage
    if (container.scrollLeft < minScroll || container.scrollLeft > maxScroll) {
      container.scrollLeft = minScroll;
    }

    let direction = directionRef.current;
    scrollPositionRef.current = container.scrollLeft;

    const STEP_PX = (AUTO_SCROLL_SPEED * STEP_MS) / 1000;

    const intervalId = setInterval(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Si l'utilisateur scrolle, on ne touche à rien
      if (userScrollingRef.current) return;

      let position = scrollPositionRef.current + direction * STEP_PX;

      if (position <= minScroll) {
        position = minScroll;
        direction = 1;
      } else if (position >= maxScroll) {
        position = maxScroll;
        direction = -1;
      }

      scrollPositionRef.current = position;
      container.scrollLeft = position;
      directionRef.current = direction;
    }, STEP_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [cartsList]);

  const poolsWithCartCount = ALL_POOLS.filter((pool, poolSchemeIndex) => {
    if (pool?.skip === true) return false;
    return cartsList.some((cart) => {
      const lastStep = cart.history?.[cart.history.length - 1];
      if (!lastStep) return false;
      return getSchemeId(lastStep.pool_id) === poolSchemeIndex && lastStep.done === false;
    });
  }).length;

  return (
    <Panel title="LINE OVERVIEW (DIGITAL TWIN)" width="full" className="rounded-md bg-grid relative" noPadding={true}>
      {
        onPoolClick === null &&
          <div className="absolute bottom-6 left-4 bg-perception-blue rounded-md text-sm px-2 py-1 text-white">
            Occupied baths: {poolsWithCartCount}/{ALL_POOLS.length}
          </div>
      }
      <div className="w-full h-full overflow-x-auto p-4"
           ref={scrollContainerRef}
           onWheel={handleUserInteraction}
           onMouseDown={handleUserInteraction}
           onTouchStart={handleUserInteraction}
      >
        <div className={`h-full flex flex-col justify-around ${positionMode === 'up' ? 'items-end' : 'items-start flex-col-reverse'} min-w-max`}>
          <DiorLine pools={POOLS_LINE_1} cartsList={cartsList} positionMode={positionMode} registerPoolRef={registerPoolRef} onPoolClick={onPoolClick} processes={processes} />
          <DiorLine pools={POOLS_LINE_2} cartsList={cartsList} startPoolSchemeIndex={25} positionMode={positionMode} registerPoolRef={registerPoolRef} onPoolClick={onPoolClick} processes={processes} />
        </div>
      </div>
    </Panel>
  );
};